import { inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { CanActivateFn, Router, UrlTree } from "@angular/router";
import { CookieService } from "ngx-cookie-service";

import { UserService } from "../services/user.service";
import { LoggerService } from "../../shared/services/logger.service";
import { UserProfileResponse } from "../../shared/models/request/user-profile.response";
import { CONSTANTS, COOKIE, SPLUNK } from "../../modules/login/util/constants";
import { Constants } from "../../shared/constants/constants";

export const autoLoginGuard: CanActivateFn = (): Observable<boolean | UrlTree> | boolean | UrlTree => {

  const userService = inject(UserService);
  const cookieService = inject(CookieService);
  const loggerService = inject(LoggerService);
  const router = inject(Router);

  const isUserNameAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_NAME);
  const isSessionIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_SID);
  const isJwtTokenAvailable: boolean = cookieService.check(COOKIE.DP_ADM_TOKEN);
  const isUserIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_ID);

  if (isUserNameAvailable && isSessionIdAvailable && isJwtTokenAvailable && isUserIdAvailable) {
    if (userService.getUserName) {
      return router.createUrlTree(['home']);
    } else {
      try {
        // Restore Session ID
        const sid: string = atob(cookieService.get(COOKIE.DP_ADM_SID));
        userService.setSessionID = sid;

        // Restore JWT Token
        const token: string = atob(cookieService.get(COOKIE.DP_ADM_TOKEN));
        userService.setJwtToken = token;

        // Restore User Id
        const userId: string = atob(cookieService.get(COOKIE.DP_ADM_USER_ID));
        userService.setUserIdentifier = userId;

        // Restore User Name
        const userName: string = atob(cookieService.get(COOKIE.DP_ADM_USER_NAME));
        userService.setUserName = userName;
        const request = { userName: userName };

        // Restore User Data
        return userService.userProfileRequest(request)
          .pipe(
            map((data: UserProfileResponse) => {
              if (data?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
                // Set user profile
                userService.setUserProfile = data.responseBody;
                // Call session timeout logic
                userService.handleSessionTimeout();
                // Set user as authenticated
                userService.setAuthenticated = true;
                userService.isAuntheticated$.next(true);

                // Splunk Update
                loggerService.logEvent(
                  SPLUNK.USER_PROFILE_SUCCESS_EVENT_CD,
                  SPLUNK.USER_PROFILE_SUCCESS_EVENT_DESC,
                  SPLUNK.USER_PROFILE_SUCCESS_ID,
                  '',
                  {
                    adminUserId: userService.getUserIdentifier,
                    adminUserName: userService.getUserName
                  },
                  CONSTANTS.SUCCESS
                );

                return router.createUrlTree(['home']);
              } else {
                return navigateToLogin(userService, loggerService, userName);
              }
            }), catchError(() => {
              return of(navigateToLogin(userService, loggerService, userName));
            })
          );
      } catch (error) {
        return of(navigateToLogin(userService, loggerService, Constants.UNAUTHUSER));
      }
    }
  } else {
    userService.setAuthenticated = false;
    userService.isAuntheticated$.next(false);
    return true;
  }
};

const navigateToLogin = (
  userService: UserService,
  loggerService: LoggerService,
  userName: string
): boolean | UrlTree => {
  loggerService.logEvent(
    SPLUNK.USER_PROFILE_FAILURE_EVENT_CD,
    SPLUNK.USER_PROFILE_FAILURE_EVENT_DESC,
    SPLUNK.USER_PROFILE_FAILURE_ID,
    '',
    {
      adminUserName: userName
    },
    CONSTANTS.FAILURE
  );
  userService.isAuntheticated$.next(false);
  userService.logoutUser();
  return true;
}
